import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Wrapper for individual component previews inside a section.
 *
 * @param {Object} props
 * @param {string} props.label - Component name/label (rendered as monospace badge)
 * @param {string} [props.description] - Optional description below label
 * @param {string} [props.className] - Additional classes for outer container
 * @param {string} [props.previewClassName] - Additional classes for the preview area
 * @param {React.ReactNode} props.children - The rendered component preview
 */
export function ShowcaseItem({ label, description, className, previewClassName, children }) {
  return (
    <div className={cn('mb-6', className)}>
      <div className="mb-2 flex items-baseline gap-2">
        <code className="rounded bg-primary-lightest px-2 py-0.5 text-xs font-bold text-primary">{label}</code>
        {description && <span className="text-xs text-neutral-dark">{description}</span>}
      </div>
      <div className={cn('rounded-lg border border-neutral-lightest bg-neutral-bright p-6', previewClassName)}>{children}</div>
    </div>
  );
}

/**
 * Section wrapper — used by every section file.
 * Applies the anchor id and consistent header styling.
 */
export function ShowcaseSection({ id, title, description, children }) {
  return (
    <section
      id={id}
      data-showcase-section
      className="mb-12 scroll-mt-20"
    >
      <header className="mb-4 border-b border-neutral-lightest pb-2">
        <h2 className="text-2xl font-extrabold text-primary">{title}</h2>
        {description && <p className="mt-1 text-sm text-neutral-dark">{description}</p>}
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
